/**
 * Stack navigator: renders every route in the stack as a card, with only the
 * focused card displayed. Pushed screens stay mounted underneath, as before.
 *
 * The card markup mirrors @react-navigation/stack 6.3 on the web (which has no
 * transition animations there), so layout, stacking and the rendered DOM stay
 * the same as before the react-router migration.
 */
import * as React from 'react'
import {
  Animated,
  Dimensions,
  Easing,
  Keyboard,
  LayoutChangeEvent,
  StyleSheet,
  View,
  ViewStyle
} from 'react-native'
import {
  Descriptor,
  Group,
  NavigationProp,
  Screen,
  useNavigationBuilder
} from '../core/NavigationBuilder'
import { NavigationState, StackRouter } from '../core/routers'

const BACKGROUND_COLOR = 'rgb(242, 242, 242)'
// Height of the (always hidden) floating header container on the web.
const DEFAULT_HEADER_HEIGHT = 64

interface StackNavigatorProps {
  id?: string
  initialRouteName?: string
  screenOptions?: object | ((props: any) => object)
  children: React.ReactNode
  // Unknown navigator props are ignored, as react-navigation did.
  [prop: string]: any
}

function StackNavigator({
  id,
  initialRouteName,
  screenOptions,
  children
}: StackNavigatorProps) {
  const { state, descriptors, navigation } = useNavigationBuilder(StackRouter, {
    id,
    initialRouteName,
    children,
    screenOptions
  })

  return (
    <StackView
      state={state}
      descriptors={descriptors}
      navigation={navigation}
    />
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function createStackNavigator<
  ParamList = Record<string, object | undefined>
>() {
  return { Navigator: StackNavigator, Screen, Group }
}

// ---------------------------------------------------------------------------
// View
// ---------------------------------------------------------------------------

interface Layout {
  width: number
  height: number
}

const getInitialLayout = (): Layout => {
  const { width = 0, height = 0 } = Dimensions.get('window')
  return { width, height }
}

function StackView({
  state,
  descriptors
}: {
  state: NavigationState
  descriptors: Record<string, Descriptor>
  navigation: any
}) {
  const [layout, setLayout] = React.useState<Layout>(getInitialLayout)

  const handleLayout = React.useCallback((e: LayoutChangeEvent) => {
    const { height, width } = e.nativeEvent.layout
    setLayout(current =>
      current.width === width && current.height === height
        ? current
        : { width, height }
    )
  }, [])

  const focusedRoute = state.routes[state.index]

  return (
    <View style={styles.container}>
      <View style={[safeAreaStyles.container, undefined]}>
        <View
          style={[{ flex: 1, backgroundColor: BACKGROUND_COLOR }, undefined]}
        >
          <View style={cardStackStyles.container} onLayout={handleLayout}>
            {state.routes.map((route, index, self) => (
              <View
                key={route.key}
                style={StyleSheet.absoluteFill}
                pointerEvents="box-none"
              >
                <Card
                  active={index === self.length - 1}
                  focused={focusedRoute.key === route.key}
                  layout={layout}
                  navigation={descriptors[route.key].navigation}
                >
                  {descriptors[route.key].render()}
                </Card>
              </View>
            ))}
          </View>
          <View
            pointerEvents="box-none"
            style={StyleSheet.flatten<ViewStyle>([
              cardStackStyles.floating,
              [
                { height: DEFAULT_HEADER_HEIGHT },
                cardStackStyles.absolute
              ] as any
            ])}
          />
        </View>
      </View>
    </View>
  )
}

function Card({
  active,
  focused,
  layout,
  navigation,
  children
}: {
  active: boolean
  focused: boolean
  layout: Layout
  navigation: NavigationProp
  children: React.ReactNode
}) {
  // A card that opens on a focused screen dismisses the keyboard, i.e. blurs
  // the focused text input (this also undoes `autoFocus` inside the screen),
  // as react-navigation's stack did on every page change.
  React.useLayoutEffect(() => {
    if (navigation.isFocused()) {
      Keyboard.dismiss()
    }
    // The open transition react-navigation ran on every card mount (its
    // default web spec, ScaleFromCenterAndroid). Nothing is interpolated from
    // it on the web, but it keeps animation-frame timing as before.
    const animation = Animated.timing(new Animated.Value(0), {
      toValue: 1,
      duration: 400,
      easing: Easing.bezier(0.35, 0.45, 0, 1),
      useNativeDriver: false,
      isInteraction: false
    })
    animation.start()
    return () => animation.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <View style={{ opacity: 1 }} collapsable={false} />
      <View
        pointerEvents="box-none"
        style={[
          {
            // This is necessary to avoid unfocused larger pages increasing scroll area
            overflow: active ? undefined : 'hidden',
            // Hide unfocused screens (also hides them from assistive technology)
            display: focused ? 'flex' : 'none'
          },
          StyleSheet.absoluteFill
        ]}
      >
        <View pointerEvents="box-none" style={StyleSheet.absoluteFill} />
        <View
          style={StyleSheet.flatten([cardStyles.container])}
          pointerEvents="box-none"
        >
          <View style={StyleSheet.flatten([cardStyles.container])}>
            <CardSheet
              layout={layout}
              style={[{ backgroundColor: BACKGROUND_COLOR }, undefined]}
            >
              <View style={cardContainerStyles.container}>
                <View style={cardContainerStyles.scene}>{children}</View>
                <View pointerEvents="box-none" />
              </View>
            </CardSheet>
          </View>
        </View>
      </View>
    </>
  )
}

function CardSheet({
  layout,
  style,
  children
}: {
  layout: Layout
  style: any
  children: React.ReactNode
}) {
  const [fill, setFill] = React.useState(false)

  React.useEffect(() => {
    if (typeof document === 'undefined' || !document.body) {
      // Only run when DOM is available
      return
    }

    const width = document.body.clientWidth
    const height = document.body.clientHeight

    setFill(width === layout.width && height === layout.height)
  }, [layout.height, layout.width])

  return (
    <View
      pointerEvents="box-none"
      style={[fill ? cardSheetStyles.page : cardSheetStyles.card, style]}
    >
      {children}
    </View>
  )
}

export type StackNavigationProp = NavigationProp

const styles = StyleSheet.create({
  container: {
    flex: 1
  }
})

const safeAreaStyles = StyleSheet.create({
  container: {
    flex: 1
  }
})

const cardStackStyles = StyleSheet.create({
  container: {
    flex: 1
  },
  absolute: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0
  },
  floating: {
    zIndex: 1
  }
})

const cardStyles = StyleSheet.create({
  container: {
    flex: 1
  }
})

const cardSheetStyles = StyleSheet.create({
  page: {
    minHeight: '100%'
  },
  card: {
    flex: 1,
    overflow: 'hidden'
  }
})

const cardContainerStyles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column-reverse'
  },
  scene: {
    flex: 1
  }
})

/** Props a stack navigator passes to its screen components. */
export type StackScreenProps<
  ParamList extends Record<string, any>,
  RouteName extends keyof ParamList = keyof ParamList
> = {
  navigation: StackNavigationProp
  route: {
    key: string
    name: RouteName
    path?: string
    params: ParamList[RouteName]
  }
}
